<template>
  <section
    v-if="activeShareType === SHARE_TYPE.EMBED_CODE"
    id="layout"
    class="flex min-w-[552px] w-[552px] h-full flex-1 flex-col items-start justify-start gap-4 overflow-y-auto overflow-x-hidden pr-2"
  >
    <section class="flex flex-col items-start gap-2">
      <UITextSmMedium>{{ t("form.embedlayoutType") }}</UITextSmMedium>
      <div class="flex items-start gap-4">
        <div
          v-for="card in CARD_LAYOUTS"
          :key="card.id"
          class="flex cursor-pointer flex-col items-center justify-center gap-2"
        >
          <UITooltip :style="{ maxWidth: '300px' }" trigger="hover" placement="top">
            <template #trigger>
              <div
                class="card-layout-container flex h-[80px] w-[120px]"
                :class="[
                  { 'border-active': isActiveLayout(card) },
                  { 'border border-red-400': showError.layout },
                  card.class,
                  { 'justify-end': card.isRightAligned },
                ]"
                @click="onLayoutClick(card)"
              >
                <div class="flex-shrink-0 rounded bg-[#344054]" :class="card.iconClass"></div>
              </div>
            </template>
            {{ card.description }}
          </UITooltip>

          <div class="flex items-center gap-1">
            <UITextSmNormal>
              {{ card.name }}
            </UITextSmNormal>
          </div>
        </div>
      </div>
    </section>
    <div
      class="w-full flex flex-col gap-2 self-stretch"
      v-if="selectedLayout.id !== INLINE"
    >
      <div
        v-if="Object.keys(selectedLayout).includes('allowMinimize')"
        class="flex items-center gap-[48px] self-stretch"
      >
        <div class="flex flex-col items-center gap-2 self-stretch">
          <div class="flex items-start gap-2 self-stretch">
            <UICheckbox
              :id="selectedLayout.id"
              v-model:checked="selectedLayout.allowMinimize"
              class="h-4 w-4"
            />
            <UITextSmNormal>{{ t("form.allowMinimize") }}</UITextSmNormal>
          </div>
          <UIInput
            v-model="selectedLayout.minimizedTitle"
            size="medium"
            class=""
            :placeholder="t('form.minimizedTitlePlaceholder')"
            name="title"
            :disabled="!selectedLayout.allowMinimize"
          />
        </div>
        <div
          class="flex w-[356px] flex-col items-start justify-center gap-2 self-stretch"
        >
          <UITextSmNormal>{{ t("form.choosePosition") }}</UITextSmNormal>
          <div class="flex items-center gap-3">
            <div
              class="leftside-position cursor-pointer"
              :class="[selectedLayout.isLeftAligned ? 'border-active' : '']"
              @click="
                () => {
                  selectedLayout.isLeftAligned = true;
                  selectedLayout.isRightAligned = false;
                }
              "
            >
              <div
                class="h-6 w-3 flex-shrink-0 rounded-[3px] bg-[#344054]"
              ></div>
            </div>
            <div
              class="rightside-position cursor-pointer"
              :class="[selectedLayout.isRightAligned ? 'border-active' : '']"
              @click="
                () => {
                  selectedLayout.isLeftAligned = false;
                  selectedLayout.isRightAligned = true;
                }
              "
            >
              <div
                class="h-6 w-3 flex-shrink-0 rounded-[3px] bg-[#344054]"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="w-fit flex flex-col items-start gap-2"
        v-if="selectedLayout.id !== INLINE"
      >
        <div class="flex items-start gap-2 self-stretch">
          <UITextSmNormal>{{ t("form.modalHeight") }}</UITextSmNormal>
        </div>
        <div class="flex items-center gap-2 w-[58%]">
          <UIInputNumber
            id="modal-height"
            v-model="modalHeight"
            size="medium"
            :min="1"
            :placeholder="t('form.modalHeightPlaceholder')"
            name="modalHeight"
            type="number"
          />
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text, vue/no-v-html -->
          <span class="text-sm min-w-fit">px</span>
        </div>
      </div>
    </div>

    <section id="triggerType" class="flex flex-col items-start gap-1 self-stretch">
      <UITextSmMedium>{{ t("form.triggerType") }}</UITextSmMedium>
      <div v-for="trigger in TRIGGERS" :key="trigger.id" class="flex">
        <UIRadio
          :id="trigger.id"
          :checked="selectedTriggerType === trigger.id"
          :label="trigger.name"
          :value="selectedTriggerType"
          @change="onRadioButtonClick(trigger.id, 'trigger')"
        />
        <div v-if="trigger.showInput" class="flex items-center">
          <UIInputNumber
            :id="trigger.id"
            v-model="fields[trigger.id]"
            :show-button="false"
            :name="trigger.name"
            :min="1"
            size="small"
            placeholder=""
            :disabled="selectedTriggerType !== trigger.id"
            type="number"
            :class="[
              showError.trigger && selectedTriggerType === trigger.id ? 'border-red-500' : '',
              'mx-2 h-7 !w-12',
            ]"
            @update:model-value="validateInput(fields[trigger.id], 'trigger')"
          /><span>{{ trigger.id === "showOnScrolling" ? "%" : t("common.seconds") }}</span>
          <span
            v-if="showError.trigger && selectedTriggerType === trigger.id"
            class="ml-2 text-sm text-red-500"
          >
            {{ errorMessage }}
          </span>
        </div>
      </div>
    </section>
    <section id="activationOption" class="flex flex-col items-start gap-1 self-stretch">
      <UITextSmMedium>{{ t("form.activationOptions") }}</UITextSmMedium>
      <div v-for="activation in ACTIVATION_OPTIONS" :key="activation.id" class="flex">
        <UIRadio
          :id="activation.id"
          :checked="selectedActivation === activation.id"
          :label="activation.name"
          :value="activation.id"
          @change="onRadioButtonClick(activation.id, 'activation')"
        />
        <div v-if="activation.showInput" class="flex items-center">
          <UIInputNumber
            :id="activation.id"
            v-model="fields[activation.id]"
            :show-button="false"
            :name="activation.name"
            :min="1"
            size="small"
            placeholder=""
            type="number"
            :class="[
              showError.activation && selectedActivation === activation.id ? 'border-red-500' : '',
              'mx-2 h-7 w-12',
            ]"
            @update:model-value="validateInput(fields[activation.id], 'activation')"
          />
          <span>{{ t("form.visit") }}</span>
          <span
            v-if="showError.activation && selectedActivation === activation.id"
            class="ml-2 text-sm text-red-500"
            >{{ errorMessage }}</span
          >
        </div>
      </div>
    </section>
    <section id="DeactivationOption" class="flex flex-col items-start gap-1 self-stretch">
      <UITextSmMedium>{{ t("form.deactivationOptions") }}</UITextSmMedium>
      <div v-for="activation in DEACTIVATION_OPTIONS" :key="activation.id" class="flex">
        <UIRadio
          :id="activation.id"
          :checked="selectedDeactivation === activation.id"
          :label="activation.name"
          :value="activation.id"
          @change="onRadioButtonClick(activation.id, 'deactivation')"
        />
        <div v-if="activation.showInput" class="flex items-center">
          <UIInputNumber
            :id="activation.id"
            v-model="fields[activation.id]"
            :name="activation.name"
            size="small"
            :show-button="false"
            :min="1"
            placeholder=""
            type="number"
            :class="[
              showError.deactivation && selectedDeactivation === activation.id
                ? 'border-red-500'
                : '',
              'mx-2 h-7 w-12',
            ]"
            @update:model-value="validateInput(fields[activation.id], 'deactivation')"
          />
          <span>{{ t("form.times") }}</span>
          <span
            v-if="showError.deactivation && selectedDeactivation === activation.id"
            class="ml-2 text-sm text-red-500"
            >{{ errorMessage }}</span
          >
        </div>
      </div>
    </section>
    <CookieConsentSettings
      class="pb-12"
      :enabled="checkForCookieConsent"
      :provider="cookieConsentProvider"
      @update:enabled="onCookieConsentToggle"
      @update:provider="cookieConsentProvider = $event"
    />
    <section
      class="flex items-end justify-end gap-2 self-stretch absolute bottom-0 right-0"
    >
      <UIButton
        id="openFormLink"
        type="primary"
        size="medium"
        :disabled="disableCopyEmbedBtn"
        @click="copyEmbedCode"
      >
        <Copy01Icon class="h-5 w-5"></Copy01Icon>
        <div class="ml-2">{{ t("form.copyEmbedCode") }}</div>
      </UIButton>
    </section>
  </section>
</template>
<script setup lang="ts">
import { useAppStore } from "@/store/app";
import {
  buildEmbedCopyArtifacts,
  buildEmbedCode,
  getEmbedScriptBaseUrl,
  type EmbedLayout,
  type StoredEmbedSettings,
} from "@/util/embedCode";
import { openLinkInNewTab, sleep } from "@/util/methods";
import { getShareUrl } from "@/util/shareLinks";
import { Copy01Icon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UIButton,
  UICheckbox,
  UIInput,
  UIInputNumber,
  UIRadio,
  UITextSmMedium,
  UITextSmNormal,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import { reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import config from "./../../../config/index";
import {
  ACTIVATION_OPTIONS,
  CARD_LAYOUTS,
  COOKIE_CONSENT_ENABLED_DEFAULT,
  COOKIE_CONSENT_PROVIDER_DEFAULT,
  DEACTIVATION_OPTIONS,
  INLINE,
  POLITE_SLIDE_IN,
  POPUP,
  SHARE_TYPE,
  STICKY_SIDEBAR,
  TRIGGERS,
} from "./../../../util/constants";
import CookieConsentSettings from "./CookieConsentSettings.vue";
import { emitPendoEvent } from "./../../../util/pendoConfig";
const { t } = useI18n();

const store = useAppStore();

const props = defineProps({
  widgetName: {
    type: String,
    required: true,
  },
  widgetId: {
    type: String,
    required: true,
  },
  formName: {
    type: String,
    default: "",
  },
  formStyle: Object,
  modelActive: {
    type: Boolean,
    default: false,
  },
  isSurvey: {
    type: Boolean,
  },
  formLayoutSettings: Object,
  isWhiteLabelled: {
    type: Boolean,
    default: false,
  },
  baseUrl: {
    type: String,
    default: "",
  },
});

const baseUrl = ref(props.baseUrl);
const embedUrl = ref("");
const iframeUrl = ref("");
const cards = ref(CARD_LAYOUTS);
const selectedLayout = ref(CARD_LAYOUTS[3]);
const MAX_MODAL_HEIGHT_PX = 500;
const fields = reactive({
  showOnScrolling: "",
  showAfter: "",
  activateOnVisit: "",
  deactivateAfter: "",
});
const showError = ref({
  layout: false,
  trigger: false,
  activation: false,
  deactivation: false,
});
const showHoverText = ref({});
const errorMessage = ref("");
const disableCopyEmbedBtn = ref(false);
const selectedTriggerType = ref("alwaysShow");
const selectedActivation = ref("alwaysActivated");
const selectedDeactivation = ref("neverDeactivate");
const modalHeight = ref(500);
const checkForCookieConsent = ref(COOKIE_CONSENT_ENABLED_DEFAULT);
const cookieConsentProvider = ref<string>(COOKIE_CONSENT_PROVIDER_DEFAULT);
const showModalNative = ref(props.modelActive);
const formLayoutSettings = reactive(props.formLayoutSettings || {});
const activeShareType = ref(1);

setupLayout();

watch(
  () => showModalNative.value,
  (newValue) => {
    emit("update:showModel", newValue);
  }
);

const emit = defineEmits(["update:showModel", "update:submit", "update:cancel", "update:close"]);

function setupLayout() {
  embedUrl.value = getShareUrl(
    baseUrl.value,
    props.widgetName,
    props.widgetId
  );

  if (!isObjectEmpty(formLayoutSettings)) {
    fillForm();
  } else {
    resetForm();
  }
}

function fillForm() {
  selectedLayout.value = formLayoutSettings.selectedLayout;
  const fieldFilling = {
    showOnScrolling:
      formLayoutSettings.selectedTriggerType === "showOnScrolling"
        ? formLayoutSettings.fields.showOnScrolling
        : "",
    showAfter:
      formLayoutSettings.selectedTriggerType === "showAfter"
        ? formLayoutSettings.fields.showAfter
        : "",
    activateOnVisit:
      formLayoutSettings.selectedActivation === "activateOnVisit"
        ? formLayoutSettings.fields.activateOnVisit
        : "",
    deactivateAfter:
      formLayoutSettings.selectedDeactivation === "deactivateAfter"
        ? formLayoutSettings.fields.deactivateAfter
        : "",
  };
  Object.assign(fields, fieldFilling);

  selectedTriggerType.value = formLayoutSettings.selectedTriggerType;
  selectedActivation.value = formLayoutSettings.selectedActivation;
  selectedDeactivation.value = formLayoutSettings.selectedDeactivation;
  checkForCookieConsent.value =
    formLayoutSettings.checkForCookieConsent ?? COOKIE_CONSENT_ENABLED_DEFAULT;
  cookieConsentProvider.value =
    formLayoutSettings.cookieConsentProvider ?? COOKIE_CONSENT_PROVIDER_DEFAULT;
  resetCards();
}
function resetForm() {
  selectedLayout.value = CARD_LAYOUTS[3];
  fields.showOnScrolling = "";
  fields.showAfter = "";
  fields.activateOnVisit = "";
  fields.deactivateAfter = "";

  showError.value = { layout: false, trigger: false, activation: false, deactivation: false };
  showHoverText.value = {};
  errorMessage.value = undefined;
  selectedTriggerType.value = "alwaysShow";
  selectedActivation.value = "alwaysActivated";
  selectedDeactivation.value = "neverDeactivate";
  checkForCookieConsent.value = COOKIE_CONSENT_ENABLED_DEFAULT;
  cookieConsentProvider.value = COOKIE_CONSENT_PROVIDER_DEFAULT;

  resetCards();
}

function resetCards() {
  cards.value = cards.value.map((card) => {
    if (
      formLayoutSettings?.selectedLayout?.id !== card.id &&
      card.id !== INLINE &&
      card.id !== POPUP
    ) {
      card.allowMinimize = false;
      card.minimizedTitle = "";
    }
    return card;
  });
}
const onRadioButtonClick = (e, type) => {
  if (type === "trigger") {
    selectedTriggerType.value = e;
    if (fields[selectedTriggerType.value] !== "") {
      validateInput(fields[selectedTriggerType.value], type);
    } else {
      showError.value.trigger = false;
    }
  }
  if (type === "activation") {
    selectedActivation.value = e;
    if (fields[selectedActivation.value] !== "") {
      validateInput(fields[selectedActivation.value], type);
    } else {
      showError.value.activation = false;
    }
  }
  if (type === "deactivation") {
    selectedDeactivation.value = e;
    if (fields[selectedDeactivation.value] !== "") {
      validateInput(fields[selectedDeactivation.value], type);
    } else {
      showError.value.deactivation = false;
    }
  }
  enableOrDisableCopyEmbedBtn();
};

function validateInput(value, type) {
  // Convert empty string to 0 for validation
  const numValue = value === "" ? 0 : Number(value);

  if (type === "trigger") {
    if (selectedTriggerType.value === "showOnScrolling") {
      // For scrolling percentage, must be between 1-100
      showError.value.trigger = numValue < 1 || numValue > 100;
    } else if (selectedTriggerType.value === "showAfter") {
      // For show after seconds, must be at least 1
      showError.value.trigger = numValue < 1;
    }
  } else if (type === "activation") {
    // For activation visits, must be at least 1
    showError.value.activation = numValue < 1;
  } else if (type === "deactivation") {
    // For deactivation times, must be at least 1
    showError.value.deactivation = numValue < 1;
  }

  enableOrDisableCopyEmbedBtn();
}
const enableOrDisableCopyEmbedBtn = () => {
  if (Object.values(showError.value).includes(true)) {
    errorMessage.value = "Invalid input!";
    disableCopyEmbedBtn.value = true;
  } else {
    disableCopyEmbedBtn.value = false;
  }
};
const isActiveLayout = (card: any) => {
  return selectedLayout.value?.id === card.id;
};

const onCookieConsentToggle = (enabled: boolean) => {
  checkForCookieConsent.value = enabled;
};
const onLayoutClick = (card: any) => {
  if (card.id === formLayoutSettings?.selectedLayout?.id) {
    selectedLayout.value = formLayoutSettings.selectedLayout;
  } else {
    selectedLayout.value = card;
  }
  showError.value.layout = false;
};

const copyEmbedCode = () => {
  if (!disableCopyEmbedBtn.value) {
    validate();
    if (Object.values(showError.value).includes(true)) {
      return (errorMessage.value = "Please fill the required field");
    }
  }
  errorMessage.value = undefined;
  createIframeUrl();
  const artifacts = saveFormLayoutSettings();
  emitPendoEvent(
    "Copy-form-embed-code",
    artifacts.analytics as unknown as Record<string, string>
  );
  store.copyToClipBoard(iframeUrl.value);
};

const validate = () => {
  showError.value.layout = !selectedLayout.value?.id ? true : false;
  if (!showError.value.trigger) {
    showError.value.trigger =
      !fields[selectedTriggerType.value] && selectedTriggerType.value !== "alwaysShow"
        ? true
        : false;
  }
  if (!showError.value.activation) {
    showError.value.activation =
      !fields[selectedActivation.value] && selectedActivation.value !== "alwaysActivated"
        ? true
        : false;
  }
  if (!showError.value.deactivation) {
    showError.value.deactivation =
      !fields[selectedDeactivation.value] && selectedDeactivation.value === "deactivateAfter"
        ? true
        : false;
  }
};
const createIframeUrl = () => (iframeUrl.value = getContainer());

const getContainer = () => {
  let iFrameHeight;
  if (props.isSurvey) {
    iFrameHeight = document.getElementById("survey-builder-container")?.offsetHeight;
    // 60 is footer element height
    iFrameHeight = iFrameHeight + 60;
    // 126 is agency branding element height
    if (props.formStyle.acBranding) {
      iFrameHeight = iFrameHeight + 126;
    }
  }
  if (!props.isSurvey && selectedLayout.value.id === INLINE) {
    iFrameHeight = document.getElementById("the-form-builder")?.offsetHeight;
  } else {
    iFrameHeight = document.getElementById("form-builder-container")?.offsetHeight;
    if (props.formStyle.acBranding) {
      iFrameHeight = iFrameHeight + 126;
    }
  }

  return buildEmbedCode(
    {
      layout: selectedLayout.value as EmbedLayout,
      triggerType: selectedTriggerType.value,
      triggerValue: fields[selectedTriggerType.value] || "",
      activationType: selectedActivation.value,
      activationValue: fields[selectedActivation.value] || "",
      deactivationType: selectedDeactivation.value,
      deactivationValue: fields[selectedDeactivation.value] || "",
      checkForCookieConsent: checkForCookieConsent.value,
      cookieConsentProvider: cookieConsentProvider.value,
      modalHeight: modalHeight.value,
    },
    {
      embedUrl: embedUrl.value,
      scriptBaseUrl: getEmbedScriptBaseUrl({
        baseUrl: baseUrl.value,
        fallbackScriptBaseUrl: config.FORM_EMBED_URL,
        isWhiteLabelled: props.isWhiteLabelled,
        environment: process.env.NODE_ENV,
      }),
      widgetId: props.widgetId,
      formName: props.formName,
      borderRadius: props.formStyle?.border?.radius || 0,
      iframeHeight: iFrameHeight,
    }
  );
};

const saveFormLayoutSettings = () => {
  const formLayoutSettingsModified = {
    selectedLayout: selectedLayout.value,
    selectedTriggerType: selectedTriggerType.value,
    selectedActivation: selectedActivation.value,
    selectedDeactivation: selectedDeactivation.value,
    checkForCookieConsent: checkForCookieConsent.value,
    cookieConsentProvider: cookieConsentProvider.value,
    fields: {
      showOnScrolling: fields[selectedTriggerType.value],
      showAfter: fields[selectedTriggerType.value],
      activateOnVisit: fields[selectedActivation.value],
      deactivateAfter: fields[selectedDeactivation.value],
    },
  };
  Object.assign(formLayoutSettings, formLayoutSettingsModified);
  const artifacts = buildEmbedCopyArtifacts({
    widgetId: props.widgetId,
    settings: formLayoutSettings as unknown as StoredEmbedSettings,
    layoutName: formLayoutSettings.selectedLayout.name,
  });
  store.saveToLocalStorage(artifacts.storageKey, artifacts.storageValue);
  return artifacts;
};

function isObjectEmpty(objectName) {
  return objectName && Object.keys(objectName).length === 0 && objectName.constructor === Object;
}

const redirectToFunnel = async (url) => {
  copyEmbedCode();
  await sleep(1000);
  openLinkInNewTab(url);
};

watch(
  () => (store?.form as any)?.form?.formData?.form?.height,
  (height) => {
    if (typeof height !== "number" || !Number.isFinite(height)) {
      modalHeight.value = MAX_MODAL_HEIGHT_PX;
      return;
    }
    const next = Math.min(height, MAX_MODAL_HEIGHT_PX);
    if (modalHeight.value !== next) modalHeight.value = next;
  },
  { immediate: true }
);
</script>
<style scoped>
.card-layout-container {
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  background: #fff;
}
.border-active {
  border: 2px solid #155eef !important;
}
.active-bg {
  background: #eff4ff;
}
.layout-container {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  flex: 1 0 0;
  height: 720px;
  align-self: stretch;
}

.leftside-position {
  display: flex;
  width: 40px;
  height: 40px;
  padding: 6px 4px;
  align-items: center;
  gap: 8px;
  aspect-ratio: 1/1;
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  background: #fff;
}
.rightside-position {
  display: flex;
  width: 40px;
  height: 40px;
  padding: 6px 4px;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  aspect-ratio: 1/1;
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  background: #fff;
}
</style>
